import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Mail,
  MessageSquare,
  RotateCcw,
  Save,
  Star,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  formatInterviewRound,
  getInterviewRoundBadgeClass,
} from '../candidates/candidateInterviewUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { STAGES, type PipelineCard } from './kanbanShared';

export interface KanbanCardProps {
  candidate: PipelineCard;
  onMove: (id: string, direction: 'prev' | 'next') => void;
  onSaveNote: (id: string, note: string) => Promise<void>;
  onSendEmail: (id: string) => Promise<void>;
  onScheduleInterview: (candidate: PipelineCard) => void;
  onSetInterviewResult: (id: string, result: 'PASSED' | 'FAILED' | null) => Promise<void>;
  onSelect: (applicationId: string) => void;
}

const KanbanCard: React.FC<KanbanCardProps> = ({
  candidate,
  onMove,
  onSaveNote,
  onSendEmail,
  onScheduleInterview,
  onSetInterviewResult,
  onSelect,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: candidate.id,
    data: {
      type: 'Candidate',
      candidate,
    },
    animateLayoutChanges: () => false,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    touchAction: 'none',
  };
  const [noteDraft, setNoteDraft] = useState(candidate.kanbanNote || '');
  const [noteOpen, setNoteOpen] = useState(false);
  const [notePopupStyle, setNotePopupStyle] = useState<React.CSSProperties>({});
  const noteIconRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!noteOpen) return;
    const handler = (e: PointerEvent) => {
      if (noteIconRef.current && !noteIconRef.current.contains(e.target as Node)) {
        setNoteDraft(candidate.kanbanNote || '');
        setNoteOpen(false);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [candidate.kanbanNote, noteOpen]);

  const handleNoteToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (noteOpen) {
      setNoteDraft(candidate.kanbanNote || '');
      setNoteOpen(false);
      return;
    }

    setNoteDraft(candidate.kanbanNote || '');
    if (noteIconRef.current) {
      const POPUP_W = 224;
      const POPUP_H = 168;
      const rect = noteIconRef.current.getBoundingClientRect();
      const top = rect.top - POPUP_H - 8 < 8 ? rect.bottom + 4 : rect.top - POPUP_H - 8;
      const left = Math.max(8, rect.right - POPUP_W);
      setNotePopupStyle({
        position: 'fixed',
        top,
        left,
        width: POPUP_W,
        zIndex: 9999,
      });
    }
    setNoteOpen(true);
  };

  const currentStageIndex = STAGES.indexOf(candidate.stage);
  const canMovePrev = currentStageIndex > 0;
  const canMoveNext = currentStageIndex < STAGES.length - 1;

  const handleSaveNote = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void onSaveNote(candidate.id, noteDraft.trim());
    setNoteOpen(false);
  };

  const canSendEmail =
    Boolean(candidate.email) &&
    candidate.stage === 'REJECTED' &&
    !candidate.emailSentAt &&
    candidate.canSendRejectionEmail !== false;

  const handleSendEmail = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!canSendEmail) return;
    void onSendEmail(candidate.id);
  };

  const handleMove = (event: React.MouseEvent<HTMLButtonElement>, direction: 'prev' | 'next') => {
    event.stopPropagation();
    onMove(candidate.id, direction);
  };

  const handleScheduleInterview = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onScheduleInterview(candidate);
  };

  const handleSetInterviewResult = (event: Event, result: 'PASSED' | 'FAILED' | null) => {
    event.stopPropagation();
    void onSetInterviewResult(candidate.id, result);
  };

  const getScoreColor = (score?: number) => {
    if (!score && score !== 0) return 'text-text-subtle';
    if (score >= 85) return 'text-success';
    if (score >= 70) return 'text-info';
    return 'text-destructive';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group relative mb-2.5 rounded-xl border border-border-subtle bg-card p-3 transition-[colors,box-shadow,transform] hover:border-primary/50 hover:shadow-md ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      onClick={() => onSelect(candidate.applicationId)}
    >
      <div className="mb-2.5 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/10 bg-primary-light text-xs font-bold text-primary">
            {candidate.name.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-start gap-1.5">
              <div className="wrap-break-word min-w-0 text-[12px] font-semibold leading-4 text-text-main transition-colors group-hover:text-primary">
                {candidate.name}
              </div>
            </div>
            <div className="mt-0.5 wrap-break-word text-[10px] leading-3.5 text-text-subtle">
              {candidate.role}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            onClick={(event) => handleMove(event, 'prev')}
            disabled={!canMovePrev}
            variant="ghost"
            size="icon-sm"
            className="rounded p-1 text-text-subtle hover:bg-bg-main hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Move to previous stage"
          >
            <ChevronLeft size={14} />
          </Button>
          <Button
            type="button"
            onClick={(event) => handleMove(event, 'next')}
            disabled={!canMoveNext}
            variant="ghost"
            size="icon-sm"
            className="rounded p-1 text-text-subtle hover:bg-bg-main hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Move to next stage"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>

      <div className="flex min-w-0 items-center justify-between gap-1 border-t border-border-subtle/50 pt-2.5">
        <div className="flex shrink-0 items-center gap-1">
          <div
            className={`flex h-5 w-11 items-center justify-center gap-1 rounded border border-border-subtle bg-bg-main px-1.5 py-0.5 ${getScoreColor(candidate.score)}`}
          >
            <Star size={9} fill="currentColor" />
            <span className="text-[11px] font-bold leading-none">{candidate.score ?? '--'}</span>
          </div>
          {candidate.stage === 'INTERVIEW' &&
          candidate.nextInterviewId &&
          candidate.nextInterviewRound ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                  className={`flex h-5 w-11 items-center justify-center rounded border px-1.5 py-0.5 text-[9px] font-bold leading-none transition-colors hover:border-primary/40 hover:text-primary ${getInterviewRoundBadgeClass(
                    candidate.nextInterviewResult,
                  )}`}
                  aria-label="Update interview result"
                  title="Update interview result"
                >
                  {formatInterviewRound(candidate.nextInterviewRound)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-32"
                onClick={(event) => event.stopPropagation()}
              >
                <DropdownMenuItem
                  className="cursor-pointer gap-2 text-success focus:text-success"
                  onSelect={(event) => handleSetInterviewResult(event, 'PASSED')}
                >
                  <CheckCircle2 size={13} /> Passed
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 text-destructive focus:text-destructive"
                  onSelect={(event) => handleSetInterviewResult(event, 'FAILED')}
                >
                  <XCircle size={13} /> Failed
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 text-text-subtle"
                  onSelect={(event) => handleSetInterviewResult(event, null)}
                >
                  <RotateCcw size={13} /> Reset
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        <div className="flex min-w-0 shrink-0 items-center justify-end gap-1 text-text-subtle">
          <Button
            ref={noteIconRef}
            type="button"
            onClick={handleNoteToggle}
            onPointerDown={(event) => event.stopPropagation()}
            variant="ghost"
            size="icon-sm"
            className="h-5 w-5 rounded p-0 text-text-subtle hover:bg-bg-main hover:text-primary"
            aria-label="Note"
          >
            <MessageSquare size={12} />
          </Button>
          {noteOpen &&
            createPortal(
              <div
                style={notePopupStyle}
                className="rounded-md border border-border-subtle bg-card p-2 shadow-lg"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <Textarea
                  value={noteDraft}
                  onChange={(event) => setNoteDraft(event.target.value)}
                  placeholder="Write note..."
                  autoFocus
                  className="min-h-20 w-full resize-none rounded border border-border-subtle bg-bg-main/40 p-2 text-sm text-text-main"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    onClick={handleSaveNote}
                    size="sm"
                    className="inline-flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90"
                  >
                    <Save size={10} /> Save
                  </Button>
                </div>
              </div>,
              document.body,
            )}
          {candidate.stage === 'REJECTED' && candidate.email ? (
            <Button
              type="button"
              onClick={handleSendEmail}
              disabled={!canSendEmail}
              variant="ghost"
              size="icon-sm"
              className="h-5 w-5 rounded p-0 text-text-subtle hover:bg-bg-main hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Send email"
              title={candidate.emailSentAt ? 'Email already sent' : 'Send rejection email'}
            >
              <Mail size={12} />
            </Button>
          ) : null}
          {candidate.stage === 'INTERVIEW' ? (
            <Button
              type="button"
              onClick={handleScheduleInterview}
              onPointerDown={(event) => event.stopPropagation()}
              variant="ghost"
              size="icon-sm"
              className="h-5 w-5 rounded p-0 text-primary hover:bg-primary-light hover:text-primary"
              aria-label="Set interview"
              title="Set interview"
            >
              <CalendarDays size={14} />
            </Button>
          ) : null}
          <div className="shrink-0 whitespace-nowrap text-[10px] font-medium">
            {candidate.appliedDate}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
